import { notify } from '@bynder/design-system';
import { useRef, useState, useEffect, useMemo } from 'react';
import { useInspectorStore } from '../stores/inspectorStore';
import { useUploaderStore } from '../stores/uploaderStore';
import { useActiveSection, useActiveWidget } from '../queries/guides';
import { CardWidget } from '../types';
import {
  useUploader as useAssetUploader,
  useUploads,
} from '@bynder/asset-frontend-sdk';
import { acceptedImageTypes } from '../constants/upload';
import {
  MAX_ALLOWED_IMAGE_SIZE,
  MAX_ALLOWED_SIZE_IN_MB,
} from '../components/widgets/ImageWidget';
import { UploadedAssetIsTooBigNotification } from '../types/notifications';
import { processFileAsImage } from '../helpers/upload';
import { retrieveBrandsApi, saveMediaApi } from '../helpers/api/uploadApi';
import FileSelector from '../components/uploader/FileSelector';
import Uploader from '../components/uploader/Uploader';
import { isDevelopment } from '../constants/app';

export enum UploadType {
  Image = 'image',
  Card = 'card',
  Background = 'background',
  PageBackground = 'pageBackground',
  GuideCover = 'GuideCover',
}

export const useUploader = (uploadType: UploadType) => {
  const [isRegistered, setIsRegistered] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isError, setIsError] = useState(false);
  const [progress, setProgress] = useState(0);
  const [estimatedSpeed, setEstimatedSpeed] = useState(0);
  const fileSelectorRef = useRef(null);
  const activeWidget = useActiveWidget() as CardWidget;
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const activeSection = useActiveSection()?.activeSection;
  const setUploadingImage = useUploaderStore.use.setUploadingImage();

  const handleClick = () => {
    fileSelectorRef.current.uploadInput.click();
  };

  // New Asset SDK Implementation

  // Legacy Implementation
  const LegacyBynderUploader: React.FC<{
    children: React.ReactNode;
    onUploadCompleted: (fileId: string) => void;
  }> = ({ children, onUploadCompleted }) => {
    const [imageObj, setImageObj] = useState<File>(null);
    const [uploadedFile, setUploadedFile] = useState(null);

    const onAssetFilesSelected = async (files: File[]) => {
      switch (uploadType) {
        case UploadType.Image:
          setUploadingImage({ id: widgetSettings?.widgetId });
          break;
        case UploadType.Card:
          setUploadingImage({
            id: activeWidget.cards[widgetSettings?.widgetIndex].id,
          });
          break;
        case UploadType.Background:
          setUploadingImage({ id: activeSection.id });
          break;
        case UploadType.PageBackground:
        case UploadType.GuideCover:
        default:
          break;
      }
      const file = files[0];

      if (file.size > MAX_ALLOWED_IMAGE_SIZE) {
        notify(
          UploadedAssetIsTooBigNotification(file.name, MAX_ALLOWED_SIZE_IN_MB),
        );
      } else {
        const image = await processFileAsImage(file);
        setUploadedFile(image);
      }
    };

    const handleUploadCompleted = async (fileId: string) => {
      const brands = await retrieveBrandsApi();
      const brandId = brands[0].id;

      const { data: saveMedia } = await saveMediaApi(
        fileId,
        brandId,
        uploadedFile,
      );
      setIsCompleted(true);
      handleDone(saveMedia.mediaid);
    };

    const handleDone = (mediaId: string) => {
      onUploadCompleted(mediaId);
      setUploadedFile(null);
    };

    useEffect(() => {
      if (!uploadedFile) return () => {};

      async function getFileFromBlob(fileUrl: string) {
        await fetch(fileUrl)
          .then(r => r.blob())
          .then(blobFile =>
            setImageObj(
              new File([blobFile], uploadedFile?.filename, {
                type: 'image/png',
              }),
            ),
          )
          .catch(e => {
            throw new Error(e);
          });
      }

      getFileFromBlob(uploadedFile?.fileBlob);
    }, [uploadedFile?.fileBlob, uploadedFile?.filename, uploadedFile]);

    return (
      <>
        <FileSelector
          active
          onFilesSelected={onAssetFilesSelected}
          multiple={false}
          contentTypes={acceptedImageTypes}
          ref={fileSelectorRef}
        >
          <div />
        </FileSelector>
        <Uploader
          onFileUploadProgress={(_fileId: string, uploadProgress: number) =>
            setProgress(uploadProgress)
          }
          onEstimatedSpeedUpdate={(bytesPerSecond: number) =>
            setEstimatedSpeed(bytesPerSecond)
          }
          onFileUploadRegistered={() => setIsRegistered(true)}
          onFileUploadConfirmed={() => setIsConfirmed(true)}
          onFileUploadError={() => setIsError(false)}
          onFileUploadCompleted={handleUploadCompleted}
          intent="upload_main_uploader_asset"
          isPublic={isDevelopment}
          files={imageObj ? [imageObj] : []}
        >
          {() => children}
        </Uploader>
      </>
    );
  };

  const BynderUploader = LegacyBynderUploader;

  return {
    BynderUploader,
    handleClick,
    progress,
    estimatedSpeed,
    isRegistered,
    isCompleted,
    isConfirmed,
    isError,
  };
};

export const useNewBynderUploader = (onUploadCompleted, uploadType) => {
  const { createUpload, startUpload } = useAssetUploader();
  const [currentUploadId, setCurrentUploadId] = useState<string>('');
  const [processedImageData, setProcessedImageData] = useState(null);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isError, setIsError] = useState(false);
  const [progress, setProgress] = useState(0);
  const setUploadingImage = useUploaderStore.use.setUploadingImage();
  const activeWidget = useActiveWidget() as CardWidget;
  const activeSection = useActiveSection()?.activeSection;
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const fileSelectorRef = useRef(null);
  const handleClick = () => {
    fileSelectorRef.current.uploadInput.click();
  };
  const uploads = useUploads();
  // Memoize the callback to prevent unnecessary re-renders
  const stableOnUploadCompleted = useMemo(
    () => onUploadCompleted,
    [onUploadCompleted],
  );

  const onAssetFilesSelected = async (files: File[]) => {
    // Reset states for new upload
    setCurrentUploadId('');
    setProcessedImageData(null);
    setIsUploadComplete(false);
    setIsError(false);
    setIsRegistered(false);
    setIsConfirmed(false);
    setIsCompleted(false);

    switch (uploadType) {
      case UploadType.Image:
        setUploadingImage({ id: widgetSettings?.widgetId });
        break;
      case UploadType.Card:
        setUploadingImage({
          id: activeWidget.cards[widgetSettings?.widgetIndex].id,
        });
        break;
      case UploadType.Background:
        setUploadingImage({ id: activeSection.id });
        break;
      case UploadType.PageBackground:
      case UploadType.GuideCover:
      default:
        break;
    }

    const file = files[0];

    if (file.size > MAX_ALLOWED_IMAGE_SIZE) {
      notify(
        UploadedAssetIsTooBigNotification(file.name, MAX_ALLOWED_SIZE_IN_MB),
      );
    } else {
      try {
        const processedImage = await processFileAsImage(file);
        setProcessedImageData(processedImage);

        // Fetch the blob from the URL (like legacy implementation)
        const response = await fetch(processedImage.fileBlob);
        const blob = await response.blob();
        
        const imageFile = new File(
          [blob], // Use the actual blob, not the URL string
          processedImage.filename,
          { type: 'image/png' },
        );

        const uploadId = createUpload(imageFile);

        if (!uploadId) {
          throw new Error('Failed to create upload: No upload ID returned');
        }

        console.log('Setting currentUploadId to:', uploadId);
        setCurrentUploadId(uploadId);
        console.log('About to call startUpload with ID:', uploadId);
        startUpload(uploadId);
        console.log('startUpload called successfully');
      } catch (error) {
        console.error('Upload creation failed:', error);
        setIsError(true);
        notify({
          variant: 'error',
          title: 'Upload Failed',
          description: 'Failed to create upload. Please try again.',
          isPersistent: false,
        });
      }
    }
  };

  // Watch for upload completion
  useEffect(() => {
    if (!currentUploadId || isUploadComplete) return;

    console.log('Checking upload completion for ID:', currentUploadId);
    console.log('Available uploads:', uploads);

    const currentUpload = uploads.find(
      upload => upload.uploadId === currentUploadId,
    );

    console.log('Found current upload:', currentUpload);
    console.log('Upload status:', currentUpload?.status);
    console.log('Upload progress:', currentUpload?.progress);
    console.log('Upload assetId:', currentUpload?.assetId);
    console.log('Upload fileId:', currentUpload?.fileId);

    // Add timeout to detect if upload is stuck
    if (currentUpload?.status === 'PENDING' && currentUpload.progress === 0) {
      const timeout = setTimeout(() => {
        console.warn(
          'Upload seems stuck in PENDING status for more than 10 seconds',
        );
        console.warn('Upload details:', currentUpload);
      }, 10000);

      return () => clearTimeout(timeout);
    }

    // Check for assetId which indicates the upload has been processed into an asset
    if (
      currentUpload?.status === 'UPLOADED' &&
      currentUpload?.assetId &&
      currentUpload.fileId &&
      processedImageData
    ) {
      console.log('Upload completed, calling handleUploadComplete');
      setIsUploadComplete(true);

      const handleUploadComplete = async () => {
        try {
          const brands = await retrieveBrandsApi();
          const brandId = brands[0].id;

          const { data: saveMedia } = await saveMediaApi(
            currentUpload.fileId,
            brandId,
            processedImageData,
          );
          stableOnUploadCompleted(saveMedia.mediaid);
        } catch (error) {
          console.error('Error in handleUploadComplete:', error);
          setIsError(true);
          notify({
            variant: 'error',
            title: 'Upload Failed',
            description: 'Failed to save uploaded file. Please try again.',
            isPersistent: false,
          });
        }
      };

      handleUploadComplete();
    } else if (currentUpload?.status === 'FAILED') {
      console.error('Upload failed:', currentUpload);
      setIsError(true);
      notify({
        variant: 'error',
        title: 'Upload Failed',
        description: 'Upload failed. Please try again.',
        isPersistent: false,
      });
    }
  }, [
    processedImageData,
    currentUploadId,
    isUploadComplete,
    stableOnUploadCompleted,
    uploads,
  ]);

  // Update states based on current upload status
  useEffect(() => {
    if (!currentUploadId) return;

    const currentUpload = uploads.find(
      upload => upload.uploadId === currentUploadId,
    );
    if (currentUpload) {
      console.log('Upload status update:', {
        uploadId: currentUpload.uploadId,
        status: currentUpload.status,
        progress: currentUpload.progress,
        assetId: currentUpload.assetId,
        fileId: currentUpload.fileId,
      });

      setIsRegistered(
        currentUpload.status === 'UPLOADING' || !!currentUpload.assetId,
      );
      setIsConfirmed(currentUpload.status === 'UPLOADED');
      setIsCompleted(currentUpload.status === 'UPLOADED');
      setIsError(currentUpload.status === 'FAILED');
      setProgress(currentUpload.progress || 0);
    }
  }, [currentUploadId, uploads]);

  const ByUploader = () => {
    return (
      <>
        <FileSelector
          active
          onFilesSelected={onAssetFilesSelected}
          multiple={false}
          contentTypes={acceptedImageTypes}
          ref={fileSelectorRef}
        >
          <div />
        </FileSelector>
      </>
    );
  };

  return {
    handleClick,
    BynderUploader: ByUploader,
    progress,
    isRegistered,
    isCompleted,
    isConfirmed,
    isError,
  };
};
